"use client"

import type { ReactNode } from "react"
import { AlertCircle } from "lucide-react"

import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"

/**
 * Shared visual shell for every shadcn-themed Payload field.
 *
 * Every primitive field component (text, number, email, …) wraps its
 * input in this shell so we get a consistent ADIS visual rhythm:
 *   • required-marker dot on the label
 *   • description text rendered below the input
 *   • inline error pill with lucide AlertCircle when validation fails
 *
 * The shell is itself a client component because the surrounding field
 * components are clients (they need `useField`). Pure markup, no state.
 */
export function FieldShell({
  label,
  required,
  description,
  error,
  showError,
  children,
  className,
  htmlFor,
}: {
  label?: ReactNode
  required?: boolean
  description?: ReactNode
  error?: string
  showError?: boolean
  children: ReactNode
  className?: string
  htmlFor?: string
}) {
  const hasError = Boolean(showError && error)
  return (
    <div
      className={cn(
        "field-type adis-field flex flex-col gap-1.5 mb-5",
        hasError && "adis-field--error",
        className,
      )}
      data-shadcn-field
    >
      {label ? (
        <Label htmlFor={htmlFor} className="text-[12px] font-semibold tracking-tight text-foreground">
          {label}
          {required ? (
            <span
              className="ml-0.5 inline-block text-destructive"
              aria-label="champ obligatoire"
            >
              *
            </span>
          ) : null}
        </Label>
      ) : null}

      {children}

      {description ? (
        <p className="text-[11.5px] leading-relaxed text-muted-foreground">
          {description}
        </p>
      ) : null}

      {hasError ? (
        <p
          role="alert"
          className="inline-flex items-center gap-1 text-[12px] font-medium text-destructive"
        >
          <AlertCircle aria-hidden className="size-3.5" />
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  )
}

/**
 * Resolve a Payload-config "label" prop (which can be a string, a
 * localised `{ fr: "...", en: "..." }` object, or undefined) into a
 * single string for display. Defaults to the field name in title-case.
 */
export function resolveLabel(
  raw: unknown,
  fallback: string,
  locale: string = "fr",
): string {
  if (typeof raw === "string") return raw
  if (raw && typeof raw === "object") {
    const localised = raw as Record<string, unknown>
    const v = localised[locale] ?? localised.fr ?? localised.en
    if (typeof v === "string") return v
  }
  // Title-case the field name as a last-resort label
  return fallback
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}
